import { create } from 'zustand'

/**
 * Chrome state: theme, rail, drawer, command palette, density, mobile nav.
 *
 * Nothing here is server data — that all lives in the query cache. This store
 * exists so a keyboard shortcut in one corner of the app can open the drawer
 * in another without threading props through the shell.
 */

export type Theme = 'light' | 'dark'
export type Density = 'comfortable' | 'compact'

const THEME_KEY = 'acs.theme'
const DENSITY_KEY = 'acs.density'
const WIDE_COLUMNS_KEY = 'acs.wide-columns'
const AUTO_SCROLL_HITS_KEY = 'acs.auto-scroll-hits'

/* ------------------------------------------------------------------ *
 * Persistence (four keys, written by hand — `persist` middleware would
 * serialise the whole store, including the drawer and palette state that
 * should not survive a reload).
 * ------------------------------------------------------------------ */

function readStored(key: string): string | null {
  try {
    return localStorage.getItem(key)
  } catch {
    // Safari private mode / storage disabled. The UI still works, it just
    // forgets the preference.
    return null
  }
}

function writeStored(key: string, value: string): void {
  try {
    localStorage.setItem(key, value)
  } catch {
    /* ignore */
  }
}

/**
 * The inline script in `index.html` already resolved the theme and stamped
 * `<html class="dark" data-theme="dark">` before first paint. Read *that*
 * rather than consulting matchMedia again: a second resolution can disagree
 * with the painted DOM (stored preference vs OS preference) and the store
 * would then flip the theme a frame after load — the exact flash the inline
 * script exists to prevent.
 */
function readInitialTheme(): Theme {
  if (typeof document === 'undefined') return 'dark'
  const stamped = document.documentElement.dataset['theme']
  if (stamped === 'light' || stamped === 'dark') return stamped
  return document.documentElement.classList.contains('dark') ? 'dark' : 'light'
}

function readInitialDensity(): Density {
  return readStored(DENSITY_KEY) === 'compact' ? 'compact' : 'comfortable'
}

/**
 * "Show every column, and let the table scroll sideways."
 *
 * **On by default**, at every width, which is the opposite of how a responsive
 * table usually works — so it is worth saying why.
 *
 * Dropping columns as the window narrows assumes the ones dropped are the ones
 * you can spare. On this screen they are not: an operator on a phone opened the
 * customer list to read Workers, Job and Schedule, and those are exactly the
 * columns the narrow tiers shed first. A phone asking for the desktop site was
 * measured at ~620px — below the 768px table threshold — so what it used to get
 * was the card list, which has no columns at all. Every route to the data an
 * operator went there for was closed by the layout being helpful.
 *
 * Scrolling sideways is the honest adaptation. Nothing is hidden, Actions stays
 * pinned so Start and Stop are always reachable, and a swipe is a gesture a
 * phone is better at than a mouse.
 *
 * Turning it OFF restores width-fitted behaviour — tiers on a desktop, cards on
 * a phone — for anyone who would rather have that. It is a preference and not a
 * media query because no query separates a phone in desktop mode from a small
 * window: it would have to key on `pointer: coarse`, and what Safari and Chrome
 * each report for that in desktop mode is precisely where the two diverge.
 *
 * Stored as '0'/'1' with ABSENT meaning on, so the default can be changed here
 * without stranding anyone who already chose.
 */
function readInitialWideColumns(): boolean {
  return readStored(WIDE_COLUMNS_KEY) !== '0'
}

/**
 * "Scroll the customer table to a row the moment it goes ACTIVE."
 *
 * **On by default** — one sighting at a time, following it is exactly what
 * the operator wants. It is a preference because a wave is not one sighting:
 * ten customers going hot a few frames apart is ten scrolls, and the table
 * swings back and forth under the operator's hand. Off, the table stays where
 * it is put; the highlight still marks every hot row either way.
 *
 * Stored as '0'/'1' with ABSENT meaning on, like `readInitialWideColumns`.
 */
function readInitialAutoScrollHits(): boolean {
  return readStored(AUTO_SCROLL_HITS_KEY) !== '0'
}

/** Single writer for every theme side effect, so they cannot drift apart. */
function applyTheme(theme: Theme): void {
  if (typeof document !== 'undefined') {
    const root = document.documentElement
    root.classList.toggle('dark', theme === 'dark')
    root.dataset['theme'] = theme
    // Keeps native controls, scrollbars and form widgets in step with the
    // token palette.
    root.style.colorScheme = theme
  }
  writeStored(THEME_KEY, theme)
}

/* ------------------------------------------------------------------ *
 * Store
 * ------------------------------------------------------------------ */

export interface UiState {
  theme: Theme
  setTheme: (theme: Theme) => void
  toggleTheme: () => void

  railCollapsed: boolean
  toggleRail: () => void

  /**
   * The slide-over navigation shown below `md`, where there is no rail track.
   * Deliberately not persisted: a sheet that is open on load is a sheet the
   * operator has to dismiss before they can see anything.
   */
  mobileNavOpen: boolean
  setMobileNavOpen: (open: boolean) => void

  /** Session whose log drawer is open, or `null` when it is closed. */

  commandOpen: boolean
  setCommandOpen: (open: boolean) => void

  density: Density
  setDensity: (density: Density) => void

  /** See `readInitialWideColumns`. Only the customers table reads it. */
  wideColumns: boolean
  toggleWideColumns: () => void

  /** See `readInitialAutoScrollHits`. Read by the customers table's ACTIVE-hit scroll. */
  autoScrollHits: boolean
  toggleAutoScrollHits: () => void
}

export const useUiStore = create<UiState>()((set, get) => ({
  theme: readInitialTheme(),
  setTheme: (theme) => {
    if (get().theme === theme) return
    applyTheme(theme)
    set({ theme })
  },
  toggleTheme: () => {
    const next: Theme = get().theme === 'dark' ? 'light' : 'dark'
    applyTheme(next)
    set({ theme: next })
  },

  railCollapsed: false,
  toggleRail: () => set((state) => ({ railCollapsed: !state.railCollapsed })),

  mobileNavOpen: false,
  setMobileNavOpen: (open) => {
    // Guarded so the "close on navigate" effect — which fires on every route
    // change — cannot publish a no-op update to every subscriber.
    if (get().mobileNavOpen === open) return
    set({ mobileNavOpen: open })
  },


  commandOpen: false,
  setCommandOpen: (open) => set({ commandOpen: open }),

  density: readInitialDensity(),
  setDensity: (density) => {
    if (get().density === density) return
    writeStored(DENSITY_KEY, density)
    set({ density })
  },

  wideColumns: readInitialWideColumns(),
  toggleWideColumns: () => {
    const next = !get().wideColumns
    writeStored(WIDE_COLUMNS_KEY, next ? '1' : '0')
    set({ wideColumns: next })
  },

  autoScrollHits: readInitialAutoScrollHits(),
  toggleAutoScrollHits: () => {
    const next = !get().autoScrollHits
    writeStored(AUTO_SCROLL_HITS_KEY, next ? '1' : '0')
    set({ autoScrollHits: next })
  },
}))
